import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './CouponModal.module.scss';

const TABS = { REGISTER: 'register', LIST: 'list' };

const COUPON_ROOT_ID = 'coupon-widget-root';

function CouponModal({ onClose }) {
  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else if (window.unmountWidgetByName) window.unmountWidgetByName(COUPON_ROOT_ID);
  }, [onClose]);
  const [tab, setTab] = useState(TABS.REGISTER);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [toast, setToast] = useState(null);
  const inputRef = useRef(null);
  const toastTimer = useRef(null);

  // 본인인증 체크 — danal_user_auth로 인증 상태 확인 + 미인증 시 인증 팝업
  const [authReady, setAuthReady] = useState(false);
  useEffect(() => {
    if (typeof window.danal_user_auth === 'function') {
      window.danal_user_auth(
        () => setAuthReady(true),           // 인증 성공
        (code) => {
          if (code === 0) setAuthReady(true); // 이미 인증됨
          else handleClose();                 // 인증 취소/실패 → 모달 닫기
        }
      );
    } else {
      setAuthReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCoupons = useCallback(async () => {
    // TODO: 어드민 할당 쿠폰 API 연동 시 구현
    setCoupons([]);
    setActiveCount(0);
  }, []);

  useEffect(() => {
    if (tab === TABS.LIST) loadCoupons();
  }, [tab, loadCoupons]);

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed || loading) return;

    setLoading(true);
    try {
      const res = await fetch('/coupon/redeem/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ code: trimmed }),
      });
      const data = await res.json();

      if (data.status === 'success') {
        setCode('');
        showToast('success', data.message || '쿠폰 등록이 성공적으로 완료되었습니다.');
      } else {
        showToast('error', data.message || '유효한 쿠폰번호가 아닙니다.');
      }
    } catch {
      showToast('error', '일시적인 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  // 본인인증 완료 대기
  if (!authReady) return null;

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        {/* Nav */}
        <div className={styles.nav}>
          <h2 className={styles.navTitle}>쿠폰</h2>
          <button type="button" className={styles.closeBtn} onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#171719" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${tab === TABS.REGISTER ? styles.tabActive : ''}`}
            onClick={() => setTab(TABS.REGISTER)}
          >
            쿠폰 등록
          </button>
          <button
            type="button"
            className={`${styles.tab} ${tab === TABS.LIST ? styles.tabActive : ''}`}
            onClick={() => setTab(TABS.LIST)}
          >
            보유한 쿠폰
          </button>
        </div>

        {/* Register Tab */}
        {tab === TABS.REGISTER && (
          <div className={styles.tabContent}>
            <p className={styles.desc}>적용할 쿠폰번호를 입력해주세요</p>
            <div className={styles.inputWrap}>
              <input
                ref={inputRef}
                type="text"
                className={styles.input}
                placeholder="쿠폰번호를 입력해주세요"
                maxLength={20}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRedeem(); }}
                autoComplete="off"
              />
            </div>
            <div className={styles.action}>
              <button
                type="button"
                className={`${styles.btnPrimary} ${!code.trim() ? styles.btnDisabled : ''}`}
                disabled={!code.trim() || loading}
                onClick={handleRedeem}
              >
                {loading ? '등록 중...' : '쿠폰 등록'}
              </button>
            </div>
          </div>
        )}

        {/* List Tab — 사용완료 제외, 활성+만료만 표시 */}
        {tab === TABS.LIST && (
          <div className={styles.tabContent}>
            <p className={styles.desc}>
              {activeCount === 0
                ? '보유한 쿠폰이 없습니다'
                : `총 ${activeCount}개의 사용가능한 쿠폰이 있습니다`}
            </p>
            <div className={styles.list}>
              {coupons.map((c, i) => (
                <div key={i} className={styles.listItem}>
                  <div className={styles.listInfo}>
                    <span className={styles.listName}>{c.name}</span>
                    <span className={styles.listDate}>쿠폰 사용 유효기간 ~{c.expire_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
          {toast.type === 'success' ? (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="11" fill="#1ED45A" />
              <path d="M6 11l3.5 3.5L16 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="11" fill="#FF4242" />
              <path d="M11 7v4M11 14h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

export default CouponModal;
